import React, { useState } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

interface JsonViewerProps {
    data: any;
    defaultCollapsedDepth?: number;
    maxHeight?: string;
    theme?: 'light' | 'dark';
}

interface JsonNodeProps {
    data: any;
    keyName?: string;
    depth: number;
    isLast: boolean;
    defaultCollapsedDepth: number;
    theme: 'light' | 'dark';
    searchTerm: string;
}

const JsonViewer = React.memo(
    function JsonViewer({
                            data,
                            defaultCollapsedDepth = 0,
                            maxHeight = '600px',
                            theme = 'light'
                        }: JsonViewerProps) {

        const themes = {
            dark: {
                background: '#ffffff',
                // background: '#1e1e1e',
                surface: '#252526',
                border: '#3e3e42',
                text: '#d4d4d4',
                textSecondary: '#858585',
                key: '#9cdcfe',
                string: '#ce9178',
                number: '#b5cea8',
                boolean: '#569cd6',
                null: '#858585',
                bracket: '#ffd700',
                hover: '#2a2d2e',
                searchBg: '#3e3e42',
                searchText: '#ffffff',
                highlight: '#ffeb3b'
            },
            light: {
                background: '#ffffff',
                surface: '#f8f9fa',
                border: '#e1e4e8',
                text: '#24292e',
                textSecondary: '#6a737d',
                key: '#005cc5',
                string: '#22863a',
                number: '#005cc5',
                boolean: '#d73a49',
                null: '#6a737d',
                bracket: '#e36209',
                hover: '#f6f8fa',
                searchBg: '#f3f4f6',
                searchText: '#1f2937',
                highlight: '#fff176'
            }
        };

        const currentTheme = themes[theme];

        const styles: Record<string, React.CSSProperties> = {
            container: {
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '14px',
                backgroundColor: currentTheme.background,
                border: `1px solid ${currentTheme.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                width: '100%'
            },
            content: {
                padding: '16px',
                maxHeight,
                overflowY: 'auto' as const,
                overflowX: 'auto' as const,
                lineHeight: '1.6',
                width: '100%'
            }
        };

        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <JsonNode
                        data={data}
                        depth={0}
                        isLast={true}
                        defaultCollapsedDepth={defaultCollapsedDepth}
                        theme={theme}
                        searchTerm=""
                    />
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Only re-render if data or display options change
        return (
            JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
            prevProps.defaultCollapsedDepth === nextProps.defaultCollapsedDepth &&
            prevProps.maxHeight === nextProps.maxHeight &&
            prevProps.theme === nextProps.theme
        );
    }
);

const JsonNode: React.FC<JsonNodeProps> = ({
                                               data,
                                               keyName,
                                               depth,
                                               isLast,
                                               defaultCollapsedDepth,
                                               theme,
                                               searchTerm
                                           }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const themes = {
        dark: {
            background: '#1e1e1e',
            surface: '#252526',
            border: '#3e3e42',
            text: '#d4d4d4',
            textSecondary: '#858585',
            key: '#9cdcfe',
            string: '#ce9178',
            number: '#b5cea8',
            boolean: '#569cd6',
            null: '#858585',
            bracket: '#ffd700',
            hover: '#2a2d2e',
            searchBg: '#3e3e42',
            searchText: '#ffffff',
            highlight: '#ffeb3b'
        },
        light: {
            background: '#ffffff',
            surface: '#f8f9fa',
            border: '#e1e4e8',
            text: '#24292e',
            textSecondary: '#6a737d',
            key: '#005cc5',
            string: '#22863a',
            number: '#005cc5',
            boolean: '#d73a49',
            null: '#6a737d',
            bracket: '#e36209',
            hover: '#f6f8fa',
            searchBg: '#f3f4f6',
            searchText: '#1f2937',
            highlight: '#fff176'
        }
    };

    const currentTheme = themes[theme];

    const getValueType = (value: any): string => {
        if (value === null) return 'null';
        if (Array.isArray(value)) return 'array';
        return typeof value;
    };

    const getValueColor = (value: any): string => {
        const type = getValueType(value);
        switch (type) {
            case 'string': return currentTheme.string;
            case 'number': return currentTheme.number;
            case 'boolean': return currentTheme.boolean;
            case 'null': return currentTheme.null;
            default: return currentTheme.text;
        }
    };

    const highlightText = (text: string): React.ReactNode => {
        if (!searchTerm || searchTerm.trim() === '') {
            return text;
        }

        const lowerText = text.toLowerCase();
        const index = lowerText.indexOf(searchTerm);

        if (index === -1) {
            return text;
        }

        const before = text.substring(0, index);
        const match = text.substring(index, index + searchTerm.length);
        const after = text.substring(index + searchTerm.length);

        return (
            <>
                {before}
                <span style={{ backgroundColor: currentTheme.highlight, color: '#000', padding: '0 2px', borderRadius: '2px' }}>
          {match}
        </span>
                {highlightText(after)}
            </>
        );
    };

    const renderValue = (value: any): React.ReactNode => {
        const type = getValueType(value);
        const color = getValueColor(value);

        if (type === 'string') {
            return (
                <span style={{
                    color,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    display: 'inline'
                }}>
          "{highlightText(value)}"
        </span>
            );
        }

        if (type === 'null') {
            return <span style={{ color, fontStyle: 'italic' }}>null</span>;
        }

        if (type === 'boolean' || type === 'number') {
            return <span style={{ color }}>{String(value)}</span>;
        }

        return null;
    };

    const isExpandable = (value: any): boolean => {
        return typeof value === 'object' && value !== null;
    };

    const getSize = (value: any): number => {
        if (Array.isArray(value)) return value.length;
        if (typeof value === 'object' && value !== null) return Object.keys(value).length;
        return 0;
    };

    const styles: Record<string, React.CSSProperties> = {
        nodeContainer: {
            marginLeft: depth > 0 ? '20px' : '0',
            borderLeft: depth > 0 ? `1px solid ${currentTheme.border}` : 'none',
            paddingLeft: depth > 0 ? '12px' : '0'
        },
        keyContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            padding: '2px 0',
            cursor: isExpandable(data) ? 'pointer' : 'default',
            borderRadius: '3px',
            transition: 'background-color var(--netorca-dur-fast) var(--netorca-ease-standard)'
        },
        expandIcon: {
            marginRight: '6px',
            color: currentTheme.textSecondary,
            fontSize: '10px',
            width: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard)'
        },
        key: {
            color: currentTheme.key,
            fontWeight: 600,
            marginRight: '8px'
        },
        colon: {
            color: currentTheme.text,
            marginRight: '8px'
        },
        bracket: {
            color: currentTheme.bracket,
            fontWeight: 700
        },
        size: {
            color: currentTheme.textSecondary,
            fontSize: '12px',
            fontStyle: 'italic',
            marginLeft: '8px'
        },
        comma: {
            color: currentTheme.text
        }
    };

    if (!isExpandable(data)) {
        return (
            <div style={styles.nodeContainer}>
                <div style={styles.keyContainer}>
                    {keyName && (
                        <>
                            <span style={styles.key}>{highlightText(keyName)}</span>
                            <span style={styles.colon}>:</span>
                        </>
                    )}
                    {renderValue(data)}
                    {!isLast && <span style={styles.comma}>,</span>}
                </div>
            </div>
        );
    }

    const isArray = Array.isArray(data);
    const entries = isArray ? data : Object.entries(data);
    const size = getSize(data);
    const openBracket = isArray ? '[' : '{';
    const closeBracket = isArray ? ']' : '}';

    return (
        <div style={styles.nodeContainer}>
            <div
                style={styles.keyContainer}
                onClick={() => setIsCollapsed(!isCollapsed)}
                onMouseEnter={(e) => {
                    if (isExpandable(data)) {
                        e.currentTarget.style.backgroundColor = currentTheme.hover;
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                {isExpandable(data) && (
                    <span style={styles.expandIcon}>
            {isCollapsed ? <RightOutlined /> : <DownOutlined />}
          </span>
                )}
                {keyName && (
                    <>
                        <span style={styles.key}>{highlightText(keyName)}</span>
                        <span style={styles.colon}>:</span>
                    </>
                )}
                <span style={styles.bracket}>{openBracket}</span>
                {isCollapsed && (
                    <>
            <span style={styles.size}>
              {size} {isArray ? 'items' : 'properties'}
            </span>
                        <span style={styles.bracket}>{closeBracket}</span>
                    </>
                )}
            </div>

            {!isCollapsed && (
                <>
                    {isArray ? (
                        entries.map((item: any, index: number) => (
                            <JsonNode
                                key={index}
                                data={item}
                                depth={depth + 1}
                                isLast={index === entries.length - 1}
                                defaultCollapsedDepth={defaultCollapsedDepth}
                                theme={theme}
                                searchTerm={searchTerm}
                            />
                        ))
                    ) : (
                        entries.map(([key, value]: [string, any], index: number) => (
                            <JsonNode
                                key={key}
                                keyName={key}
                                data={value}
                                depth={depth + 1}
                                isLast={index === entries.length - 1}
                                defaultCollapsedDepth={defaultCollapsedDepth}
                                theme={theme}
                                searchTerm={searchTerm}
                            />
                        ))
                    )}
                    <div style={{ paddingLeft: '20px' }}>
                        <span style={styles.bracket}>{closeBracket}</span>
                        {!isLast && <span style={styles.comma}>,</span>}
                    </div>
                </>
            )}
        </div>
    );
};

export default JsonViewer;